import React from 'react';
import { useAppDrawer } from './AppDrawerContext';
import { DEFAULT_TRANSITION_DURATION } from './constants';
import { BaseAppDrawer } from './styles';

/**
 * AppDrawer
 * When used for multiple features this component gives the appearance
 * that one drawer is rendered and only the contents of the drawer is changing.
 * AppDrawerContext provides state and actions for the component.
 */

type AppDrawerProps = React.ComponentPropsWithoutRef<'div'> & {
  'aria -labelledby'?: string;
  children: React.ReactNode;
  id: string;
  onClose?: (event: React.SyntheticEvent, ...args: any[]) => void;
};

export const AppDrawer: React.FC<AppDrawerProps> = ({
  id,
  children,
  onClose,
  'aria-labelledby': ariaLabelledBy,
  ...props
}) => {
  const { activeAppDrawer, activeAppDrawerBeforeOpenRef } = useAppDrawer();

  // If another drawer was already open, give the appearance of a single drawer
  // with changing content by disabling the open/close animation.
  const transitionDuration = activeAppDrawerBeforeOpenRef.current
    ? 0
    : DEFAULT_TRANSITION_DURATION;

  return (
    <BaseAppDrawer
      anchor="right"
      aria-labelledby={ariaLabelledBy}
      onClose={onClose}
      open={activeAppDrawer === id}
      role="dialog"
      transitionDuration={transitionDuration}
      variant="temporary"
      {...props}
    >
      {children}
    </BaseAppDrawer>
  );
};
